import getMatchingVariant from '../utils/variants/getMatchingVariant';
import { EVENTS } from '../utils/constants';
import updatePrice from '../utils/variants/updatePrice';
import updateSelectedVariant from '../utils/variants/updateSelectedVariants';
import updateFormControls from '../utils/variants/updateFormControls';
import updateURL from '../utils/variants/updateURL';
import preselectVariant from '../utils/variants/preselectVariant';
import updateVariantImages from '../utils/variants/updateVariantImages';
import updateProductGallery from '../utils/product/updateProductGallery';
import updateVariantAvailability from '../utils/variants/updateVariantAvailability';

// isPopup: run without the PDP-only side effects (URL, sold-out messages,
// Klaviyo) and against the popup price markup — the quick-buy drawer and the
// PDP "Complete Your Kitchen" panel, which render another product's form on
// the page. Defaults to "the drawer is open" for the legacy call sites.
const initProductVariants = (container = document.querySelector('[data-product-main]'), { isPopup } = {}) => {
  if (!container) return;
  const form = container.querySelector('[data-product-form]');
  if (!form) return;
  const variantEls = container.querySelectorAll('[data-variant-input]');

  const variantsDataEl = form.querySelector('[data-product-variants-json]');
  const optionNamesEl = form.querySelector('[data-product-options-json]');
  const variantInventoryEl = form.querySelector('[data-product-variants-inventory-policy-json]');
  const variantIdInput = form.querySelector('[data-atc-id-input]');
  const quantitySelector = form.querySelector('[data-quantity-selector]');
  const quantityInput = quantitySelector?.querySelector('[data-quantity-input]');
  const atcButton = form.querySelector('button[type="submit"]');
  const atcButtonPrice = atcButton?.querySelector('[data-atc-price]');
  const atcButtonMsg = atcButton?.querySelector('[data-atc-price-msg]');
  const atcButtonPipe = atcButton?.querySelector('[data-atc-price-pipe]');
  const productPreOrderMsg = container.querySelector('[data-product-preorder-msg]');
  const productSoldInfo = container.querySelector('[data-product-sold-info]');
  const klaviyoBIS = container.querySelector('[data-klaviyo-bis]');
  const deliveryCountdown = container.querySelector('[data-delivery-countdown]');

  let variantsData = [];
  let optionNames = [];
  let variantInventory = [];

  try {
    variantsData = JSON.parse(variantsDataEl?.textContent || '[]');
    optionNames = JSON.parse(optionNamesEl?.textContent || '[]');
    variantInventory = JSON.parse(variantInventoryEl?.textContent || '[]');
  } catch (e) {
    console.error('Invalid variant/options JSON', e);
    return;
  }

  // Single-variant products render no picker but still need the gallery
  // ordered by filename sequence (see updateProductGallery)
  if (!variantEls.length) {
    if (variantsData.length === 1) updateProductGallery(variantsData[0], container);
    return;
  }

  const selectedOptions = {};

  const isPopupMode = isPopup ?? document.querySelector('[data-atc-popup]')?.open === true;
  // Scoped to the container: the PDP, the drawer and the inline panel can all
  // hold a price block at once
  const priceContainer = container.querySelector(
    isPopupMode ? '[data-product-price-popup]' : '[data-product-price-pdp]'
  );
  const priceEl = priceContainer?.querySelector('[data-product-price]');
  const compareAtPriceEl = priceContainer?.querySelector('[data-product-compare-at-price]');
  const badgeEl = priceContainer?.querySelector('[data-product-sale-badge]');

  const updateVariantDetails = () => {
    const variant = getMatchingVariant(selectedOptions, variantsData, optionNames);
    if (!variant) return;

    variantIdInput && (variantIdInput.value = variant.id);
    updatePrice({
      priceEl,
      compareAtPriceEl,
      badgeEl,
      price: variant.price,
      compareAtPrice: variant.compare_at_price,
      badgeTemplate: window.theme.strings.priceBadgeText
    });

    if (!isPopupMode) {
      updateSelectedVariant({
        variant,
        variantInventory,
        elements: {
          productPreOrderMsg,
          productSoldInfo,
          klaviyoBIS,
          atcButtonMsg,
          deliveryCountdown
        },
        strings: window.theme.strings
      });

      updateURL({ variantID: variant.id });
      // Sections outside the product form (Complete Your Kitchen) follow the variant
      document.dispatchEvent(new CustomEvent(EVENTS.VARIANT_CHANGED, { detail: { variantId: variant.id } }));
      if (!variant.available && klaviyoBIS) {
        klaviyoBIS.dataset.unavailableVariantId = variant.id;
      }
    }

    updateFormControls({
      variant,
      qty: Number(quantityInput?.value) || 1,
      variantsData,
      optionNames,
      selectedOptions,
      atcButton,
      atcButtonPrice,
      atcButtonPipe,
      quantitySelector
    });

    updateProductGallery(variant, container);
    updateVariantImages(selectedOptions, variantsData, optionNames);
    updateVariantAvailability({ selectedOptions, variantsData, optionNames, form, strings: window.theme.strings });
  };

  const setOptionUI = (name, value) => {
    const inputEl = form.querySelector(`[data-variant-input][name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`);
    if (inputEl) inputEl.checked = true;
    const selectedValueEl = container.querySelector(`[data-option-selected-value="${CSS.escape(name)}"]`);
    if (selectedValueEl) selectedValueEl.textContent = value;
  };

  // The chosen value has no variant with the other current selections (e.g.
  // a colour that only comes in one size): move the other options to the
  // first variant that has this value, preferring one in stock, so the picker
  // never rests on a combination that doesn't exist.
  const announceOptionChanges = (changes) => {
    const statusEl = container.querySelector('[data-variant-change-status]');
    const template = window.theme.strings?.optionChanged;
    if (!statusEl || !template || !changes.length) return;
    statusEl.textContent = changes
      .map((change) => template.replace('{{ option }}', change.name).replace('{{ value }}', change.value))
      .join('. ');
  };

  const resolveMissingCombination = (name, value) => {
    // preselectVariant fills options one at a time on load — only resolve
    // once every option has a value, i.e. a real click into a missing combo
    const isFullySelected = optionNames.every((optionName) => selectedOptions[optionName]);
    if (!isFullySelected) return;
    if (getMatchingVariant(selectedOptions, variantsData, optionNames)) return;
    const optionKey = `option${optionNames.indexOf(name) + 1}`;
    const candidates = variantsData.filter((candidate) => candidate[optionKey] === value);
    const target = candidates.find((candidate) => candidate.available) || candidates[0];
    if (!target) return;

    const changes = [];
    optionNames.forEach((optionName, index) => {
      if (optionName === name) return;
      const nextValue = target[`option${index + 1}`];
      if (selectedOptions[optionName] === nextValue) return;
      selectedOptions[optionName] = nextValue;
      setOptionUI(optionName, nextValue);
      changes.push({ name: optionName, value: nextValue });
    });
    announceOptionChanges(changes);
  };

  const updateSelectedOption = (name, value) => {
    selectedOptions[name] = value;
    setOptionUI(name, value);
    resolveMissingCombination(name, value);
    updateVariantDetails();
  };

  if (variantEls.length) {
    variantEls.forEach(variant => {
      variant.addEventListener('change', ({ target }) => {
        const { name, value } = target;
        updateSelectedOption(name, value);
      });
    });
  }

  preselectVariant({
    variantsData,
    optionNames,
    form,
    updateSelectedOption,
    variantIdInput
  });
};

export default initProductVariants;
